import {
  Contact,
  LayoutGrid,
  ListTodo,
  Megaphone,
  Menu,
  PhoneIncoming,
  Settings,
  Target,
  Truck,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useCanAccess } from "ra-core";
import { useState } from "react";
import { Link, matchPath, useLocation } from "react-router";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { useMyPendingCalls } from "../callPlan/useCallPlan";
import { useWaUnread } from "../whatsapp/useWhatsapp";
import { IncomingCallSheet } from "./IncomingCallSheet";
import { WhatsappIcon } from "./WhatsappCallButton";

const Count = ({ value }: { value: number }) =>
  value > 0 ? (
    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
      {value > 99 ? "99+" : value}
    </span>
  ) : null;

const NavItem = ({
  to,
  label,
  icon: Icon,
  active,
  count = 0,
}: {
  to: string;
  label: string;
  icon: LucideIcon | typeof WhatsappIcon;
  active: boolean;
  count?: number;
}) => (
  <Link
    to={to}
    className={cn(
      "flex w-16 flex-col items-center gap-0.5 rounded-xl py-1.5",
      active ? "text-foreground" : "text-muted-foreground",
    )}
  >
    <span className="relative">
      <Icon className="h-6 w-6" />
      <Count value={count} />
    </span>
    <span className="text-[10px] font-medium">{label}</span>
  </Link>
);

/**
 * Bottom bar of the CRM on the phone, made for the setters: their day, the
 * leads, the "Me llaman" button for incoming WhatsApp calls, WhatsApp and the
 * rest.
 */
export const GulaMobileNavigation = () => {
  const location = useLocation();
  const [incomingOpen, setIncomingOpen] = useState(false);
  const pending = useMyPendingCalls();
  const waUnread = useWaUnread();
  const { canAccess: canSeeOperations = false } = useCanAccess({
    resource: "operations",
    action: "list",
  });
  const { canAccess: canSeeMoney = false } = useCanAccess({
    resource: "money",
    action: "list",
  });
  const is = (pattern: string) => !!matchPath(pattern, location.pathname);

  const isPwa =
    typeof window !== "undefined" &&
    window.matchMedia("(display-mode: standalone)").matches;
  const isIos =
    typeof navigator !== "undefined" &&
    /iPad|iPod|iPhone/.test(navigator.userAgent);

  const more: { to: string; label: string; icon: LucideIcon }[] = [
    { to: "/contacts", label: "Contactos", icon: Contact },
    { to: "/tasks", label: "Tareas", icon: ListTodo },
    {
      to: "/whatsapp/material",
      label: "Material para enviar",
      icon: LayoutGrid,
    },
    ...(canSeeOperations
      ? [{ to: "/operaciones", label: "Operaciones", icon: Truck }]
      : []),
    ...(canSeeMoney ? [{ to: "/dinero", label: "Dinero", icon: Wallet }] : []),
    ...(canSeeMoney
      ? [{ to: "/parte", label: "Parte del día (grupo)", icon: Megaphone }]
      : []),
    { to: "/settings", label: "Ajustes y salir", icon: Settings },
  ];

  return (
    <>
      <IncomingCallSheet
        open={incomingOpen}
        onClose={() => setIncomingOpen(false)}
      />
      <nav
        aria-label="Navegación"
        className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur"
        style={{
          paddingBottom:
            isPwa && isIos ? 18 : "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="mx-auto flex h-16 max-w-md items-center justify-around px-2">
          <NavItem
            to="/"
            label="Jornada"
            icon={Target}
            active={is("/") || is("/mi-jornada")}
            count={pending}
          />
          <NavItem
            to="/clientes"
            label="Clientes"
            icon={LayoutGrid}
            active={is("/clientes/*")}
          />
          <button
            type="button"
            onClick={() => setIncomingOpen(true)}
            className="-mt-6 flex flex-col items-center gap-0.5"
            aria-label="Me llaman"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-lime-400 text-neutral-900 shadow-lg ring-4 ring-background active:scale-95">
              <PhoneIncoming className="h-6 w-6" />
            </span>
            <span className="text-[10px] font-semibold">Me llaman</span>
          </button>
          <NavItem
            to="/whatsapp"
            label="WhatsApp"
            icon={WhatsappIcon}
            active={is("/whatsapp")}
            count={waUnread}
          />
          <DropdownMenu>
            <DropdownMenuTrigger className="flex w-16 flex-col items-center gap-0.5 rounded-xl py-1.5 text-muted-foreground">
              <Menu className="h-6 w-6" />
              <span className="text-[10px] font-medium">Más</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              {more.map(({ to, label, icon: Icon }) => (
                <DropdownMenuItem key={to} asChild className="h-11 text-base">
                  <Link to={to}>
                    <Icon className="h-4 w-4" />
                    {label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </>
  );
};
